import { useCallback, useEffect, useState } from "react";
import { academyRequest } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import type {
  CommercialHistoryPage,
  CommercialLicense,
  CommercialLicensePage,
  FundingArrangement,
  FundingArrangementPage,
} from "./generated/commercial-catalogue";

export const COMMERCIAL_CATALOGUE_CHANGED = "academy-commercial-catalogue-changed";

export type CommercialEntityKind = "organization" | "institution";
type CommercialDirectoryKind = "licenses" | "funding";
type CommercialRow = Record<string, string | number | boolean>;

const ROUTES: Record<CommercialEntityKind, Partial<Record<string, CommercialDirectoryKind>>> = {
  organization: { "finance-plan": "licenses" },
  institution: {
    "lab-entitlements": "licenses",
    "scholarships-funding": "funding",
  },
};

export type CommercialDirectory = {
  enabled: boolean;
  kind?: CommercialDirectoryKind;
  rows: CommercialRow[];
  loading: boolean;
  error: string;
  hasMore: boolean;
  refresh: () => void;
};

export type CommercialHistory = {
  enabled: boolean;
  items: Array<{
    id: string;
    action: string;
    reason: string;
    actorId: string;
    effectiveActorId?: string | null;
    fromStatus?: string | null;
    toStatus: string;
    version: number;
    createdAt: string;
  }>;
  loading: boolean;
  error: string;
  refresh: () => void;
};

export function isCommercialDirectory(entityKind: CommercialEntityKind, routeId: string) {
  return Boolean(ROUTES[entityKind][routeId]);
}

function isoDate(value?: string | null) {
  if (!value) return "Open ended";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleDateString();
}

function money(value: number | null | undefined, currency: string | null | undefined) {
  if (value == null || !currency) return "Not specified";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value / 100);
  } catch {
    return `${currency} ${(value / 100).toFixed(2)}`;
  }
}

function licenseRow(item: CommercialLicense): CommercialRow {
  return {
    id: item.id,
    name: item.offeringName,
    scope: item.programId,
    capacity: item.capacity,
    people: item.capacity,
    usage: "Not reported",
    progress: "Not reported",
    owner: `${item.licenseeType} license`,
    status: item.status,
    code: item.licenseKey,
    date: isoDate(item.validFrom),
    deadline: isoDate(item.validTo),
    version: `v${item.version}`,
    updated: item.createdAt ? isoDate(item.createdAt) : "Not reported",
    source: "Academy commercial catalogue",
  };
}

function fundingRow(item: FundingArrangement): CommercialRow {
  return {
    id: item.id,
    name: item.name,
    code: `Funding v${item.fundingVersion}`,
    owner: item.funderType,
    scope: item.offeringName || item.programId || "Exact commercial offering",
    status: item.status,
    progress: "Not reported",
    amount: money(item.allocationMinor ?? item.maximumAwardMinor, item.currency),
    value: item.percentage == null ? money(item.allocationMinor, item.currency) : `${item.percentage}%`,
    date: isoDate(item.validFrom),
    deadline: isoDate(item.validTo),
    version: `v${item.version}`,
    updated: item.createdAt ? isoDate(item.createdAt) : "Not reported",
    source: "Academy commercial catalogue",
  };
}

export function useCommercialDirectory(
  entityKind: CommercialEntityKind,
  routeId: string,
): CommercialDirectory {
  const identity = useIdentity();
  const sessionPresent = Boolean(identity.session);
  const kind = ROUTES[entityKind][routeId];
  const enabled = Boolean(kind);
  const [rows, setRows] = useState<CommercialRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [generation, setGeneration] = useState(0);
  const refresh = useCallback(() => setGeneration(value => value + 1), []);
  useEffect(() => {
    if (!enabled) return;
    const changed = () => refresh();
    window.addEventListener(COMMERCIAL_CATALOGUE_CHANGED, changed);
    return () => window.removeEventListener(COMMERCIAL_CATALOGUE_CHANGED, changed);
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !kind || !sessionPresent) {
      setRows([]);
      setError(enabled && !sessionPresent ? "Sign in to load current commercial records." : "");
      setHasMore(false);
      return;
    }
    const controller = new AbortController();
    let current = true;
    setLoading(true);
    setError("");
    void (async () => {
      try {
        const before = await checkIdentitySession();
        const items: Array<CommercialLicense | FundingArrangement> = [];
        let after: string | undefined;
        do {
          const path = `commercial-catalogue/${kind}${after ? `?after=${encodeURIComponent(after)}` : ""}`;
          const page =
            kind === "licenses"
              ? await academyRequest<CommercialLicensePage>(
                  path,
                  undefined,
                  "GET",
                  true,
                  undefined,
                  controller.signal,
                )
              : await academyRequest<FundingArrangementPage>(
                  path,
                  undefined,
                  "GET",
                  true,
                  undefined,
                  controller.signal,
                );
          items.push(...page.items);
          after = page.nextCursor;
        } while (after && items.length < 500);
        const afterSession = await checkIdentitySession();
        if (
          before.user.id !== afterSession.user.id ||
          before.tenantId !== afterSession.tenantId ||
          before.workspace.id !== afterSession.workspace.id
        )
          throw new Error("Your commercial workspace changed while records were loading.");
        if (!current) return;
        setRows(
          kind === "licenses"
            ? (items as CommercialLicense[]).map(licenseRow)
            : (items as FundingArrangement[]).map(fundingRow),
        );
        setHasMore(Boolean(after));
      } catch (cause) {
        if (!current || controller.signal.aborted) return;
        setRows([]);
        setHasMore(false);
        setError(cause instanceof Error ? cause.message : "Unable to load commercial records.");
      } finally {
        if (current) setLoading(false);
      }
    })();
    return () => {
      current = false;
      controller.abort();
    };
  }, [
    enabled,
    entityKind,
    generation,
    identity.session?.tenantId,
    identity.session?.user.id,
    identity.session?.workspace.id,
    kind,
    sessionPresent,
  ]);

  return { enabled, kind, rows, loading, error, hasMore, refresh };
}

export function useCommercialHistory(
  entityKind: CommercialEntityKind,
  routeId: string,
  resourceId?: string,
): CommercialHistory {
  const identity = useIdentity();
  const kind = ROUTES[entityKind][routeId];
  const enabled = Boolean(kind && resourceId);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    context: string;
    items: CommercialHistory["items"];
    error: string;
  } | null>(null);
  const context = `${identity.session?.tenantId || ""}:${identity.session?.workspace.id || ""}:${kind || ""}:${resourceId || ""}`;
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!enabled || !kind || !resourceId || !identity.session?.user.id) return;
    let current = true;
    const abort = new AbortController();
    void (async () => {
      try {
        const before = await checkIdentitySession();
        const page = await academyRequest<CommercialHistoryPage>(
          `commercial-catalogue/${kind}/${encodeURIComponent(resourceId)}/history`,
          undefined,
          "GET",
          true,
          undefined,
          abort.signal,
        );
        const after = await checkIdentitySession();
        if (
          before.user.id !== after.user.id ||
          before.tenantId !== after.tenantId ||
          before.workspace.id !== after.workspace.id
        )
          throw new Error("Your account or workspace changed while history was loading.");
        if (current)
          setState({
            context,
            error: "",
            items: page.items.map(item => ({
              id: `${resourceId}:${item.version}:${item.action}:${item.createdAt}`,
              action: item.action,
              reason: item.reason,
              actorId: item.actorId,
              effectiveActorId: null,
              fromStatus: item.fromStatus,
              toStatus: item.toStatus,
              version: item.version,
              createdAt: item.createdAt,
            })),
          });
      } catch (cause) {
        if (current && !abort.signal.aborted)
          setState({
            context,
            items: [],
            error: cause instanceof Error ? cause.message : "Unable to load commercial history.",
          });
      }
    })();
    return () => {
      current = false;
      abort.abort();
    };
  }, [context, enabled, identity.session?.user.id, kind, resourceId, revision]);
  const current = state?.context === context ? state : null;
  return {
    enabled,
    items: current?.items || [],
    loading: enabled && !current,
    error: current?.error || "",
    refresh,
  };
}
